import type {
  FileUIPart,
  LanguageModelUsage,
  ModelMessage,
  TextUIPart,
  ToolUIPart,
} from 'ai'

type MessagePart = TextUIPart | FileUIPart | ToolUIPart

/**
 * Normalized usage format for backend compatibility
 * The backend expects flat structure, not the nested v3 format
 */
export interface NormalizedUsage {
  inputTokens: number
  outputTokens: number
  totalTokens: number
}

/**
 * Normalize AI SDK usage to flat format for backend compatibility
 * Handles both v2 (flat) and v3 (nested) formats from @ai-sdk/google
 */
export function normalizeUsage(
  usage: LanguageModelUsage | undefined | null
): NormalizedUsage | undefined {
  if (!usage || typeof usage !== 'object') {
    return undefined
  }

  // Check for v3 nested format (inputTokens is an object with 'total' property)
  const usageAny = usage as Record<string, unknown>
  const inputTokens = usageAny.inputTokens
  const outputTokens = usageAny.outputTokens

  if (
    inputTokens &&
    typeof inputTokens === 'object' &&
    'total' in (inputTokens as object)
  ) {
    // v3 nested format
    const inputObj = inputTokens as { total?: number }
    const outputObj = outputTokens as { total?: number } | undefined
    const input = inputObj.total ?? 0
    const output = outputObj?.total ?? 0
    return {
      inputTokens: input,
      outputTokens: output,
      totalTokens: input + output,
    }
  }

  // v2 flat format or already normalized
  if (typeof inputTokens === 'number' || typeof outputTokens === 'number') {
    const input = (inputTokens as number) ?? 0
    const output = (outputTokens as number) ?? 0
    // Check for totalTokens property (v2 format)
    const total =
      typeof usageAny.totalTokens === 'number'
        ? usageAny.totalTokens
        : input + output
    return {
      inputTokens: input,
      outputTokens: output,
      totalTokens: total,
    }
  }

  return undefined
}

/**
 * Create an initial assistant message for streaming
 */
export function createInitialAssistantMessage(): ModelMessage & { id: string } {
  const assistantMsgId = crypto.randomUUID()
  return {
    id: assistantMsgId,
    role: 'assistant' as const,
    content: '',
    parts: [],
  } as ModelMessage & { id: string }
}

/**
 * Generate display text for user message
 */
export function generateDisplayText(
  text: string | undefined,
  files: FileUIPart[] | undefined
): string {
  if (text && text.trim().length > 0) return text
  if (files && files.length > 0) {
    return files.length === 1
      ? (files[0].filename ?? 'Attachment')
      : `${files.length} attachments`
  }
  return ''
}

/**
 * Build message parts array
 */
export function buildUserMessageParts(
  text: string | undefined,
  files: FileUIPart[] | undefined
): MessagePart[] {
  const parts: MessagePart[] = []
  if (text && text.trim().length > 0) {
    parts.push({ type: 'text', text } satisfies TextUIPart)
  }
  if (files && files.length > 0) {
    parts.push(...files)
  }
  return parts
}

/**
 * Create user message
 */
export function createUserMessage(
  displayText: string,
  parts: MessagePart[]
): ModelMessage {
  return {
    id: crypto.randomUUID(),
    role: 'user',
    content: displayText,
    ...(parts.length > 0 && { parts }),
  } as ModelMessage
}

/**
 * Generate chat title from first message
 */
export function generateChatTitle(
  text: string | undefined,
  files: FileUIPart[] | undefined
): string {
  if (text && text.trim().length > 0) {
    return text.slice(0, 50)
  }
  if (files && files.length === 1) {
    return files[0].filename ?? 'New Chat'
  }
  return 'New Chat'
}

/**
 * Build final message parts (text + tool parts)
 */
export function buildFinalMessageParts(
  finalText: string,
  toolParts: MessagePart[]
): MessagePart[] {
  const finalParts: MessagePart[] = []
  if (finalText && finalText.trim().length > 0) {
    finalParts.push({ type: 'text', text: finalText } satisfies TextUIPart)
  }
  finalParts.push(...toolParts)
  return finalParts
}

/**
 * Convert UIMessage format from backend to ModelMessage format
 */
export function toModelMessage(msg: {
  id: string
  role: string
  parts?: unknown[]
  metadata?: Record<string, unknown>
}): ModelMessage {
  const parts = msg.parts || []
  const textParts = parts.filter(
    (part): part is TextUIPart =>
      typeof part === 'object' &&
      part !== null &&
      'type' in part &&
      part.type === 'text'
  )
  const content =
    textParts.length > 0 ? textParts.map((p) => p.text).join('') : ''

  return {
    id: msg.id,
    role: msg.role,
    content,
    ...(parts.length > 0 ? { parts } : {}),
    ...(msg.metadata ? { metadata: msg.metadata } : {}),
  } as ModelMessage
}
